import { DAY, type EntitlementListInput, type EntitlementListSuccess, type EntityId } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useTantalimQuery } from '../query'
import { useServerRequest } from '../api'
import { type GetHookResponse } from '../hooks'
import { entitlementsKey } from './key'

type UseEntitlementsResponse = GetHookResponse<EntitlementListSuccess> & {
  refresh: () => Promise<void>
  isRefreshing: boolean
}

export function useEntitlements(entityId?: EntityId, enabled = true): UseEntitlementsResponse {
  const queryClient = useQueryClient()
  const serverRequest = useServerRequest<EntitlementListInput, EntitlementListSuccess>('/entitlement/list')
  const [isRefreshing, setIsRefreshing] = useState(false)

  const query = useTantalimQuery<EntitlementListInput, EntitlementListSuccess, EntitlementListSuccess>({
    input: { entityId },
    queryKey: entitlementsKey(entityId),
    url: '/entitlement/list',
    convertResponse: (data) => data,
    options: {
      staleTime: DAY,
      enabled,
    },
  })

  const refresh = useCallback(async (): Promise<void> => {
    setIsRefreshing(true)
    try {
      const result = await serverRequest({ entityId, purgeCache: true })
      queryClient.setQueryData(entitlementsKey(entityId), result)
    } finally {
      setIsRefreshing(false)
    }
  }, [entityId, serverRequest, queryClient])

  return { ...query, refresh, isRefreshing }
}
